import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStores, approveStore } from '../services/storeService';
import { createOwner } from '../services/ownerService';
import { getAdminData } from '../services/authService';
import { getStatusLabel, getStatusClass } from '../utils/storeStatus';
import { formatDate } from '../utils/dateFormat';
import { IMAGES } from '../constants/images';
import Header from '../components/common/Header';
import PageHeader from '../components/common/PageHeader';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import '../css/StoreManagement.css';
import StoreDecisionModal from '../components/common/StoreDecisionModal';

const StoreManagement = () => {
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [stores, setStores] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 0,
    size: 20,
  });
  const [adminInfo, setAdminInfo] = useState(null);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [createOwnerOpen, setCreateOwnerOpen] = useState(false);
  const [newOwner, setNewOwner] = useState({ loginId: '', password: '', name: '', phone: '' });
  const [ownerSubmitting, setOwnerSubmitting] = useState(false);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getStores(filters);
      setStores(result.stores || []);
      setTotal(result.total || 0);
    } catch (error) {
      showSnackbar(error.message || '가맹점 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    setAdminInfo(getAdminData());
  }, []);

  const handleStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status: status === prev.status ? '' : status, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value, page: 0 }));
  }, []);

  const totalPages = Math.ceil(total / filters.size);

  const openDecisionModal = (store) => {
    setSelectedStore(store);
    setDecisionModalOpen(true);
  };

  const handleCreateOwner = async (e) => {
    e.preventDefault();
    if (ownerSubmitting) return;

    const loginId = newOwner.loginId.trim();
    const name = newOwner.name.trim();
    const phone = newOwner.phone.trim();
    const password = newOwner.password;

    if (!loginId || !password || !name || !phone) {
      showSnackbar('모든 필드를 입력해주세요.', 'error');
      return;
    }
    if (!/^[a-zA-Z0-9]{4,20}$/.test(loginId)) {
      showSnackbar('아이디는 영문/숫자 4~20자여야 합니다.', 'error');
      return;
    }
    if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,20}$/.test(password)) {
      showSnackbar('비밀번호는 영문/숫자/특수문자 포함 8~20자여야 합니다.', 'error');
      return;
    }
    if (!/^01[0-9]{8,9}$/.test(phone)) {
      showSnackbar('전화번호 형식이 올바르지 않습니다. (예: 01012345678)', 'error');
      return;
    }

    setOwnerSubmitting(true);
    try {
      await createOwner({ loginId, password, name, phone });
      showSnackbar(`점주 [${name}] 계정 등록 완료`, 'success');
      setNewOwner({ loginId: '', password: '', name: '', phone: '' });
      setCreateOwnerOpen(false);
    } catch (error) {
      showSnackbar(error.message || '점주 계정 등록에 실패했습니다.', 'error');
    } finally {
      setOwnerSubmitting(false);
    }
  };

  const handleDecision = async (approved, reason = '') => {
    if (!selectedStore) return;
    if (!approved && !reason.trim()) {
      showSnackbar('반려 사유를 입력해주세요.', 'error');
      return;
    }

    setDecisionLoading(true);
    try {
      await approveStore(selectedStore.store_id, {
        approved,
        rejectReason: approved ? undefined : reason.trim(),
      });
      showSnackbar(approved ? '승인 완료되었습니다.' : '반려 처리되었습니다.', 'success');
      setDecisionModalOpen(false);
      setSelectedStore(null);
      fetchStores();
    } catch (error) {
      showSnackbar(error.message || '요청 처리에 실패했습니다.', 'error');
    } finally {
      setDecisionLoading(false);
    }
  };

  return (
    <div className="store-management">
      <Header />

      <PageHeader title="가맹점 관리" />
      <div className="store-page">
        <div className="store-content">
        <div className="store-search-bar">
          <div className="store-search-group">
            <div className="store-search-input">
              <img src={IMAGES.GLASSES} alt="" className="search-icon" aria-hidden="true" />
              <input
                type="text"
                placeholder="가맹점 이름, 사업자 등록번호, 전화번호 등 검색"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                aria-label="가맹점 검색"
              />
            </div>
            <span className="total-count" aria-live="polite" aria-atomic="true">총 {total}개</span>
          </div>
          <button
            type="button"
            className="create-owner-btn"
            onClick={() => setCreateOwnerOpen(true)}
          >
            점주 계정 추가
          </button>
        </div>

        <div className="store-table-container">
          <table className="store-table">
            <thead>
              <tr>
                <th>가맹점 이름</th>
                <th>등록일</th>
                <th>사업자등록번호</th>
                <th>영업상태</th>
                <th>승인 여부</th>
                <th>가맹점 전화번호</th>
                <th>대표자 휴대폰전화번호</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="loading-cell">
                    로딩 중...
                  </td>
                </tr>
              ) : stores.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-cell">
                    등록된 가맹점이 없습니다.
                  </td>
                </tr>
              ) : (
                stores.map((store) => (
                  <tr
                    key={store.store_id}
                    onClick={() => navigate(`/admin/management/stores/${store.store_id}`)}
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/admin/management/stores/${store.store_id}`);
                      }
                    }}
                    aria-label={`${store.store_name || '가맹점'} 상세 정보 보기`}
                  >
                    <td>{store.store_name || '-'}</td>
                    <td>{formatDate(store.created_at)}</td>
                    <td>{store.business_number || '-'}</td>
                    <td>추후 구현</td>
                    <td>
                      {store.status === 'PENDING' ? (
                        <button
                          className={`status-badge ${getStatusClass(store.status)} clickable`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDecisionModal(store);
                          }}
                          aria-label={`${store.store_name || '가맹점'} 승인 처리`}
                        >
                          {getStatusLabel(store.status)}
                        </button>
                      ) : (
                        <span className={`status-badge ${getStatusClass(store.status)}`}>
                          {getStatusLabel(store.status)}
                        </span>
                      )}
                    </td>
                    <td>{store.phone || '-'}</td>
                    <td>{store.owner_phone || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <nav className="pagination" aria-label="페이지 네비게이션">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 0}
              className="page-btn"
              aria-label="이전 페이지"
            >
              이전
            </button>
            <span className="page-info" aria-current="page">
              {filters.page + 1} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page >= totalPages - 1}
              className="page-btn"
              aria-label="다음 페이지"
            >
              다음
            </button>
          </nav>
        )}
        </div>
      </div>

      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isOpen={snackbar.isOpen}
        onClose={hideSnackbar}
        duration={3000}
      />

      {createOwnerOpen && (
        <div
          className="modal-overlay"
          onClick={() => !ownerSubmitting && setCreateOwnerOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>점주 계정 추가</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => !ownerSubmitting && setCreateOwnerOpen(false)}
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            <form className="modal-form" onSubmit={handleCreateOwner}>
              <label>
                로그인 아이디 *
                <input
                  type="text"
                  value={newOwner.loginId}
                  onChange={(e) => setNewOwner({ ...newOwner, loginId: e.target.value })}
                  placeholder="영문/숫자 4~20자"
                  autoComplete="off"
                  required
                  disabled={ownerSubmitting}
                />
              </label>
              <label>
                초기 비밀번호 *
                <input
                  type="password"
                  value={newOwner.password}
                  onChange={(e) => setNewOwner({ ...newOwner, password: e.target.value })}
                  placeholder="영문/숫자/특수문자 포함 8~20자"
                  autoComplete="new-password"
                  required
                  disabled={ownerSubmitting}
                />
              </label>
              <label>
                점주명 *
                <input
                  type="text"
                  value={newOwner.name}
                  onChange={(e) => setNewOwner({ ...newOwner, name: e.target.value })}
                  placeholder="2~20자"
                  required
                  disabled={ownerSubmitting}
                />
              </label>
              <label>
                전화번호 *
                <input
                  type="tel"
                  value={newOwner.phone}
                  onChange={(e) => setNewOwner({ ...newOwner, phone: e.target.value })}
                  placeholder="숫자만 입력 (예: 01012345678)"
                  required
                  disabled={ownerSubmitting}
                />
              </label>
              <p className="create-owner-hint">
                관리자가 점주 대신 생성하는 계정입니다. SMS 인증은 생략되며, 필수 약관은 자동으로 동의 처리됩니다.
              </p>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCreateOwnerOpen(false)}
                  disabled={ownerSubmitting}
                >
                  취소
                </button>
                <button type="submit" className="btn-primary" disabled={ownerSubmitting}>
                  {ownerSubmitting ? '등록중...' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <StoreDecisionModal
        isOpen={decisionModalOpen}
        storeName={selectedStore?.store_name}
        loading={decisionLoading}
        onClose={() => {
          if (!decisionLoading) {
            setDecisionModalOpen(false);
            setSelectedStore(null);
          }
        }}
        onSubmit={handleDecision}
      />
    </div>
  );
};

export default StoreManagement;
